import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { PominieteWskazanie, PostepRozmowy } from "@shared/recenzjeTypy.js";
import { useToast } from "../components/ui/Toast.js";
import type { WynikOczekiwania } from "../lib/autozapis.js";
import { czyBrakUwagiNaSerwerze, tekstBledu } from "../lib/recenzja.js";
import { recenzjaApi } from "../lib/recenzjaApi.js";
import { kluczSlotuUwagi, type MagazynSzkicow } from "../lib/szkiceRecenzji.js";
import {
  PUSTY_WIDOK,
  cialoDodania,
  cialoZmiany,
  czyPustaTresc,
  nowyKluczUwagi,
  poczatekUwag,
  reduktorUwag,
  uwagaZSerwera,
  wpisyBezWypowiedzi,
  zbudujWidokUwag,
  type SzkicUwagi,
  type UwagaRecenzji,
  type WidokUwag,
  type WpisUwagi,
} from "../lib/uwagiWypowiedzi.js";
import type { useAutozapis } from "./useAutozapis.js";

/**
 * Uwagi recenzenta przy wypowiedzi: lista uwag rozmowy, otwarte formularze i zapis przez TEN SAM silnik autozapisu co
 * reszta przegladu (lib/autozapis.ts): jedno pole kolejki na uwage, szeregowo, z debounce dla tekstu, z kopia szkicu
 * w sessionStorage i ostrzezeniem przed zamknieciem karty.
 *
 * Cykl zycia uwagi:
 *  - przycisk "Dodaj uwage" otwiera pusty formularz i NIC nie tworzy: uwaga powstaje dopiero z pierwszym niepustym tekstem,
 *  - pierwszy zapis to POST, zwrocone id zostaje przy polu kolejki, a kazdy dalszy zapis to PUT. Poniewaz zapisy jednego
 *    pola ida szeregowo, tekst wpisany w trakcie lotu POST-a wychodzi jako PUT po nim, nigdy jako drugi POST,
 *  - PUT, na ktory serwer odpowie 404 (uwage usunieto w innej karcie albo na innym urzadzeniu), nie przepada: uwaga
 *    powstaje od nowa POST-em z BIEZACA trescia, a zapamietane id jest podmieniane,
 *  - tresc wyczyszczona do pustej (serwer jej nie przyjmuje) NIE wychodzi na serwer i niczego nie usuwa: ani wyjscie z pola
 *    (alt-tab, klik obok, klawiatura telefonu), ani sam fakt, ze pole jest puste. Serwer zachowuje ostatnia niepusta tresc.
 *    Uwage usuwa wylacznie JAWNA decyzja recenzenta w oknie potwierdzenia: "Usun" przy karcie, "Usun uwage" w formularzu
 *    albo "Gotowe" na zapisanej uwadze z pustym polem (to samo okno). "Anuluj" w oknie niczego nie zmienia; po "Gotowe"
 *    zamyka formularz i zostawia karte z ostatnia zapisana trescia. "Gotowe" na nowej, nigdy niezapisanej uwadze z pustym
 *    polem po prostu zamyka formularz i niczego nie wysyla,
 *  - usuniecie idzie tym samym polem kolejki, wiec nigdy nie wyprzedzi zapisu, ktory jeszcze leci, a po zakolejkowaniu nic
 *    nie moze go zastapic (formularz jest wtedy tylko do odczytu, a `zapiszUwage` ignoruje zapisy). 404 przy usuwaniu to
 *    sukces: uwagi juz nie ma.
 *
 * Wspolna lista (`stan`, `widok`, `liczba`) zmienia sie WYLACZNIE, gdy wraca wynik zapisu albo usuniecia: tekst pola zyje
 * w formularzu, wiec pisanie w jednej uwadze nie przerysowuje setek wypowiedzi transkryptu ani paska postepu.
 */

/** Co kolejka wie o jednej uwadze. */
interface SlotUwagi {
  /** Id uwagi na serwerze (po pierwszym zapisie) albo null: pierwszy zapis to POST. */
  id: string | null;
  /** Ostatnia uwaga przyjeta przez serwer pod tym polem (null: jeszcze nie zapisana). Z niej powstaje okno potwierdzenia. */
  uwaga: UwagaRecenzji | null;
  /** Usuniecie tej uwagi jest zakolejkowane albo leci: nic innego nie wolno wpisac w to pole. */
  usuwa: boolean;
}

export interface AkcjeUwag {
  /** Tytuly parametrow rozmowy do listy "Czego dotyczy". */
  parametry: readonly string[];
  /** "Dodaj uwage": otwiera pusty formularz nowej uwagi do wypowiedzi (nic jeszcze nie zapisuje). */
  otworzUwage: (numer: number | null) => void;
  /** "Edytuj" przy zapisanej uwadze: otwiera jej formularz. */
  edytujUwage: (klucz: string) => void;
  /**
   * Zmiana pola: tekst z opoznieniem (debounce), parametr od razu (0). Ignorowana, gdy usuniecie tej uwagi jest juz
   * zakolejkowane (potwierdzone przez recenzenta).
   */
  zapiszUwage: (
    klucz: string,
    numer: number | null,
    tresc: { komentarz: string; parametr: string | null },
    opoznienieMs: number,
  ) => void;
  /** Blur pola: wysyla to, co czeka na przerwe w pisaniu. NIGDY nie usuwa uwagi. */
  splusczUwage: (klucz: string) => void;
  /**
   * "Gotowe": wysyla wszystko, czeka na wynik i zamyka formularz. Zwraca false, gdy formularz ma zostac otwarty: zapis
   * sie nie udal (tresc i komunikat zostaja) albo pytamy o usuniecie. Pusta tresc zapisanej uwagi NIE usuwa jej sama:
   * otwiera okno potwierdzenia (zamkniecie formularza zalezy od decyzji w oknie). Pusta tresc uwagi, ktorej serwer nigdy
   * nie przyjal, zamyka formularz i niczego nie wysyla.
   */
  zakonczUwage: (klucz: string, tresc: { komentarz: string; parametr: string | null }) => Promise<boolean>;
  /** "Usun" przy uwadze: otwiera okno potwierdzenia (usuniecie jest nieodwracalne). */
  zadajUsuniecieUwagi: (uwaga: UwagaRecenzji) => void;
}

export interface UwagiPrzegladu {
  akcje: AkcjeUwag;
  /** Ile uwag jest zapisanych na serwerze (licznik w pasku postepu). */
  liczba: number;
  /** Wpisy pod wypowiedziami (numer wypowiedzi -> wpisy), ze wspoldzieleniem struktury miedzy zmianami. */
  widok: WidokUwag;
  /** Wpisy bez wypowiedzi w transkrypcie (numer null albo spoza rozmowy): osobna karta przed notatka know-how. */
  bezWypowiedzi: readonly WpisUwagi[];
  /** Okno potwierdzenia usuniecia: `cel` null znaczy zamkniete. */
  usuwanie: { cel: UwagaRecenzji | null; potwierdz: () => void; anuluj: () => void };
}

type Kolejka = Pick<ReturnType<typeof useAutozapis>, "zaplanuj" | "splucz" | "splukajWszystko" | "poczekaj">;

interface Wejscie {
  token: string;
  sesjaId: string;
  liczbaWypowiedzi: number;
  /** Uwagi zapisane na serwerze (RecenzjaRozmowa.pominiete). */
  serwerowe: readonly PominieteWskazanie[];
  parametry: readonly string[];
  /** Szkice uwag z magazynu (SzkiceRozmowy.uwagi), czytane raz przy wejsciu na rozmowe. */
  szkice: ReadonlyMap<string, SzkicUwagi>;
  kolejka: Kolejka;
  magazyn: MagazynSzkicow;
  /** Nakladka na zapis z aktualizacja licznika postepu z odpowiedzi serwera (usePrzegladRecenzji). */
  przyjmijPostep: <T extends { postep: PostepRozmowy }>(odpowiedz: Promise<T>) => Promise<T>;
}

/** Pytanie o usuniecie: o ktora uwage i co zrobic z formularzem, gdy recenzent odpowie "Anuluj". */
interface ZadanieUsuniecia {
  uwaga: UwagaRecenzji;
  /** Pytanie padlo po "Gotowe" na uwadze z pustym polem: "Anuluj" zamyka formularz i zostawia karte z zapisana trescia. */
  zamknijFormularzPoAnulowaniu: boolean;
}

export function useUwagiRecenzji(wejscie: Wejscie): UwagiPrzegladu {
  const { token, sesjaId, liczbaWypowiedzi, serwerowe, parametry, szkice, kolejka, magazyn, przyjmijPostep } = wejscie;
  const toast = useToast();
  // Czytane raz, przy pierwszym renderze: formularze biora z tego poczatkowy stan, a efekt nizej odsyla to na serwer.
  const [poczatek] = useState(() => poczatekUwag(serwerowe, szkice, liczbaWypowiedzi));
  const [stan, dispatch] = useReducer(reduktorUwag, poczatek.stan);
  const [zadanie, setZadanie] = useState<ZadanieUsuniecia | null>(null);
  // Rejestr kolejki: jedno pole na uwage. Nie jest stanem widoku, wiec nie przerysowuje niczego.
  const [sloty] = useState(
    () =>
      new Map<string, SlotUwagi>(
        [...poczatek.ids].map(
          ([klucz, id]) =>
            [klucz, { id, uwaga: poczatek.stan.uwagi.find((u) => u.klucz === klucz) ?? null, usuwa: false }] as const,
        ),
      ),
  );
  /** Klucze uwag, ktore serwer faktycznie usunal (albo juz ich nie mial): toast sukcesu tylko dla nich. */
  const [usuniete] = useState(() => new Set<string>());
  /** Ostatnia wartosc zaplanowana dla pola: po POST-cie wiemy, czy w locie wyprzedzila go nowsza (wtedy kopia dostaje id). */
  const [ostatnie] = useState(() => new Map<string, SzkicUwagi>());
  const przywrocono = useRef(false);
  const poprzedniWidok = useRef<WidokUwag>(PUSTY_WIDOK);

  const widok = useMemo(() => {
    const nowy = zbudujWidokUwag(stan, poprzedniWidok.current);
    poprzedniWidok.current = nowy;
    return nowy;
  }, [stan]);
  const bezWypowiedzi = useMemo(() => wpisyBezWypowiedzi(widok, liczbaWypowiedzi), [widok, liczbaWypowiedzi]);

  const { zaplanuj, splucz, splukajWszystko, poczekaj } = kolejka;

  const wewnetrzne = useMemo(() => {
    const slotDla = (klucz: string): SlotUwagi => {
      let slot = sloty.get(klucz);
      if (!slot) {
        slot = { id: null, uwaga: null, usuwa: false };
        sloty.set(klucz, slot);
      }
      return slot;
    };

    /** Zadanie kolejki dla jednej uwagi. Wolane szeregowo per pole, wiec `slot.id` jest zawsze aktualne. */
    const wyslij = async (klucz: string, szkic: SzkicUwagi): Promise<void> => {
      const slot = slotDla(klucz);
      const nazwa = kluczSlotuUwagi(klucz);

      if (szkic.usun) {
        // Ustawiane tez tutaj, bo "Sprobuj ponownie" po nieudanym usunieciu wchodzi prosto w to zadanie.
        slot.usuwa = true;
        dispatch({ typ: "usuwanie", klucz, trwa: true });
        try {
          // Uwaga, ktorej serwer nigdy nie przyjal, nie ma czego usuwac po jego stronie.
          if (slot.id !== null) await przyjmijPostep(recenzjaApi.usunPominiete(token, sesjaId, slot.id));
        } catch (blad) {
          // 404: uwagi juz nie ma (usunieto ja w innej karcie albo na innym urzadzeniu), czyli cel jest osiagniety.
          if (!czyBrakUwagiNaSerwerze(blad)) {
            slot.usuwa = false;
            dispatch({ typ: "usuwanie", klucz, trwa: false });
            throw blad;
          }
        }
        sloty.delete(klucz);
        ostatnie.delete(klucz);
        usuniete.add(klucz);
        dispatch({ typ: "usunieto", klucz });
        return;
      }

      // Serwer nie przyjmuje pustej tresci: pole wyczyszczone w trakcie pisania niczego nie wysyla i niczego nie usuwa.
      if (czyPustaTresc(szkic.komentarz)) return;

      /** Pierwszy zapis uwagi, takze po 404 przy zmianie: zawsze z trescia, ktora jest teraz w kolejce. */
      const utworz = async (): Promise<PominieteWskazanie> => {
        const odpowiedz = await przyjmijPostep(recenzjaApi.dodajPominiete(token, sesjaId, cialoDodania(szkic)));
        slot.id = odpowiedz.pominiete.id;
        // Nowsza wartosc czeka w kolejce, a jej kopia w magazynie powstala przed poznaniem id: dopisujemy je, zeby
        // nieudany PUT i ponowne wejscie nie zrobily z niej drugiej uwagi.
        const nowsza = ostatnie.get(klucz);
        if (nowsza !== undefined && nowsza !== szkic) magazyn.zapisz(nazwa, { ...nowsza, id: slot.id });
        return odpowiedz.pominiete;
      };

      let zapisana: PominieteWskazanie;
      if (slot.id === null) {
        zapisana = await utworz();
      } else {
        try {
          zapisana = (await przyjmijPostep(recenzjaApi.zmienPominiete(token, sesjaId, slot.id, cialoZmiany(szkic)))).pominiete;
        } catch (blad) {
          if (!czyBrakUwagiNaSerwerze(blad)) throw blad;
          // Uwagi nie ma juz na serwerze: tekstu nie wolno stracic, wiec powstaje od nowa, a stare id przestaje obowiazywac.
          slot.id = null;
          zapisana = await utworz();
        }
      }
      slot.uwaga = uwagaZSerwera(zapisana, klucz);
      dispatch({ typ: "zapisano", uwaga: slot.uwaga });
      // Kopia z id (silnik zdejmuje tylko kopie bez zmian wzgledem tego, co zaplanowano).
      magazyn.usunJesliTa(nazwa, { ...szkic, id: slot.id });
    };

    const planuj = (klucz: string, szkic: SzkicUwagi, opoznienieMs: number): void => {
      ostatnie.set(klucz, szkic);
      zaplanuj(kluczSlotuUwagi(klucz), szkic, (w) => wyslij(klucz, w), opoznienieMs);
    };

    const planujUsuniecie = (klucz: string, numer: number | null): void => {
      const slot = slotDla(klucz);
      slot.usuwa = true;
      dispatch({ typ: "usuwanie", klucz, trwa: true });
      planuj(klucz, { numer, parametr: null, komentarz: "", id: slot.id, usun: true }, 0);
    };

    return { slotDla, planuj, planujUsuniecie, czyUsunieta: (klucz: string) => usuniete.has(klucz) };
  }, [sloty, ostatnie, usuniete, token, sesjaId, zaplanuj, magazyn, przyjmijPostep]);

  const akcje = useMemo<AkcjeUwag>(
    () => ({
      parametry,
      otworzUwage: (numer) => dispatch({ typ: "otworz-nowa", klucz: nowyKluczUwagi(), numer }),
      edytujUwage: (klucz) => dispatch({ typ: "edytuj", klucz }),
      zapiszUwage: (klucz, numer, tresc, opoznienieMs) => {
        const slot = wewnetrzne.slotDla(klucz);
        // Potwierdzone usuniecie jest ostateczne: zapis w tym samym polu kolejki zastapilby je po cichu.
        if (slot.usuwa) return;
        wewnetrzne.planuj(
          klucz,
          { numer, parametr: tresc.parametr, komentarz: tresc.komentarz, id: slot.id, usun: false },
          opoznienieMs,
        );
      },
      splusczUwage: (klucz) => splucz(kluczSlotuUwagi(klucz)),
      zakonczUwage: async (klucz, tresc) => {
        const nazwa = kluczSlotuUwagi(klucz);
        if (czyPustaTresc(tresc.komentarz)) {
          // Z pustego pola nic nie idzie na serwer. Czy uwaga w ogole tam jest, wiadomo dopiero po tym, co juz leci
          // (pierwszy POST moze byc w drodze), wiec najpierw czekamy na kolejke tego pola.
          splucz(nazwa);
          const wynik = await poczekaj(nazwa);
          if (!wynik.ok) return false;
          const zapisana = wewnetrzne.slotDla(klucz).uwaga;
          if (zapisana === null) {
            // Nowa uwaga, ktorej serwer nigdy nie przyjal: nie ma czego usuwac ani o co pytac.
            dispatch({ typ: "zamknij", klucz });
            return true;
          }
          // Zapisana uwaga z pustym polem: usunac ja moze tylko recenzent, w oknie potwierdzenia.
          setZadanie({ uwaga: zapisana, zamknijFormularzPoAnulowaniu: true });
          return false;
        }
        // "Gotowe" wysyla wszystko (takze nieudane zapisy: to ostatnia szansa przed zamknieciem) i czeka na wynik.
        splukajWszystko();
        const wynik = await poczekaj(nazwa);
        if (wynik.ok) dispatch({ typ: "zamknij", klucz });
        return wynik.ok;
      },
      zadajUsuniecieUwagi: (uwaga) => setZadanie({ uwaga, zamknijFormularzPoAnulowaniu: false }),
    }),
    [parametry, wewnetrzne, splucz, splukajWszystko, poczekaj],
  );

  const potwierdz = useCallback((): void => {
    if (zadanie === null) return;
    const { uwaga } = zadanie;
    const nazwa = kluczSlotuUwagi(uwaga.klucz);
    // Okno zamyka sie od razu (fokus wraca na przycisk "Usun", ktory zniknie razem z uwaga), a usuniecie leci polem kolejki.
    setZadanie(null);
    wewnetrzne.planujUsuniecie(uwaga.klucz, uwaga.numerWypowiedzi);
    void poczekaj(nazwa).then((wynik: WynikOczekiwania) => {
      if (!wynik.ok) {
        toast.blad(tekstBledu(wynik.blad));
        return;
      }
      // Sukces ogloszony tylko wtedy, gdy uwaga faktycznie zniknela z listy; w przeciwnym razie nie zostawiamy "Usuwam..." na zawsze.
      if (wewnetrzne.czyUsunieta(uwaga.klucz)) toast.sukces("Uwaga usunięta.");
      else dispatch({ typ: "usuwanie", klucz: uwaga.klucz, trwa: false });
    });
  }, [zadanie, wewnetrzne, poczekaj, toast]);
  const anuluj = useCallback((): void => {
    // "Anuluj" niczego nie wysyla. Po "Gotowe" z pustym polem formularz ma sie zamknac i pokazac ostatnia zapisana tresc.
    if (zadanie?.zamknijFormularzPoAnulowaniu) dispatch({ typ: "zamknij", klucz: zadanie.uwaga.klucz });
    setZadanie(null);
  }, [zadanie]);
  const cel = zadanie?.uwaga ?? null;
  const usuwanie = useMemo(() => ({ cel, potwierdz, anuluj }), [cel, potwierdz, anuluj]);

  // Szkice z poprzedniej wizyty leca na serwer od razu po wejsciu na rozmowe (raz, takze w trybie StrictMode).
  useEffect(() => {
    if (przywrocono.current) return;
    przywrocono.current = true;
    for (const klucz of poczatek.doPorzucenia) magazyn.usun(kluczSlotuUwagi(klucz));
    for (const { klucz, szkic } of poczatek.doZapisu) wewnetrzne.planuj(klucz, szkic, 0);
    for (const { klucz, szkic } of poczatek.doUsuniecia) wewnetrzne.planujUsuniecie(klucz, szkic.numer);
    // Efekt jednorazowy: wejscie na rozmowe. Dane wejsciowe sa stale w obrebie tej instancji widoku.
  }, []);

  return { akcje, liczba: stan.uwagi.length, widok, bezWypowiedzi, usuwanie };
}
